import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react';

import { registerMapRef } from './tableMenuUtils';

type UseTableItemMenuParams<TId extends string | number> = {
  getFocusTargetOnOpen?: (openId: TId) => HTMLButtonElement | null | undefined;
};

export function useTableItemMenu<TId extends string | number>({
  getFocusTargetOnOpen,
}: UseTableItemMenuParams<TId> = {}) {
  const [openItemMenuId, setOpenItemMenuId] = useState<TId | null>(null);
  const itemMenuRefs = useRef<Map<TId, HTMLDivElement>>(new Map());
  const itemMenuButtonRefs = useRef<Map<TId, HTMLButtonElement>>(new Map());
  const itemActionButtonRefs = useRef<Map<TId, HTMLButtonElement>>(new Map());

  const closeItemMenu = useCallback(() => {
    setOpenItemMenuId(null);
  }, []);

  const isItemMenuOpen = useCallback(
    (itemId: TId) => openItemMenuId === itemId,
    [openItemMenuId],
  );

  const focusMenuButton = useCallback((itemId: TId) => {
    itemMenuButtonRefs.current.get(itemId)?.focus();
  }, []);

  const focusFirstAction = useCallback((itemId: TId) => {
    const customTarget = getFocusTargetOnOpen?.(itemId);
    if (customTarget) {
      customTarget.focus();
      return;
    }
    itemActionButtonRefs.current.get(itemId)?.focus();
  }, [getFocusTargetOnOpen]);

  useEffect(() => {
    if (openItemMenuId === null) {
      return;
    }

    const handleClickOutside = (event: MouseEvent | globalThis.MouseEvent) => {
      const itemMenuRef = itemMenuRefs.current.get(openItemMenuId);
      const itemMenuButtonRef = itemMenuButtonRefs.current.get(openItemMenuId);
      const target = event.target as Node;

      if (
        itemMenuRef
        && !itemMenuRef.contains(target)
        && !itemMenuButtonRef?.contains(target)
      ) {
        setOpenItemMenuId(null);
      }
    };

    const handleEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        const itemMenuButtonRef = itemMenuButtonRefs.current.get(openItemMenuId);
        setOpenItemMenuId(null);
        itemMenuButtonRef?.focus();
      }
    };

    document.addEventListener('mousedown', handleClickOutside as unknown as EventListener);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside as unknown as EventListener);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [openItemMenuId]);

  useEffect(() => {
    if (openItemMenuId !== null) {
      focusFirstAction(openItemMenuId);
    }
  }, [openItemMenuId, focusFirstAction]);

  const handleItemMenuToggle = useCallback((itemId: TId, event: MouseEvent) => {
    event.stopPropagation();
    setOpenItemMenuId((current) => (current === itemId ? null : itemId));
  }, []);

  const handleItemMenuKeyDown = useCallback((itemId: TId, event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      setOpenItemMenuId((current) => (current === itemId ? null : itemId));
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      event.stopPropagation();
      if (openItemMenuId !== itemId) {
        setOpenItemMenuId(itemId);
      } else {
        focusFirstAction(itemId);
      }
    } else if (event.key === 'Escape' && openItemMenuId === itemId) {
      event.preventDefault();
      event.stopPropagation();
      setOpenItemMenuId(null);
      focusMenuButton(itemId);
    }
  }, [focusFirstAction, focusMenuButton, openItemMenuId]);

  const registerItemMenuRef = useCallback((itemId: TId, element: HTMLDivElement | null) => {
    registerMapRef(itemMenuRefs.current, itemId, element);
  }, []);

  const registerItemMenuButtonRef = useCallback((itemId: TId, element: HTMLButtonElement | null) => {
    registerMapRef(itemMenuButtonRefs.current, itemId, element);
  }, []);

  const registerItemActionRef = useCallback((itemId: TId, element: HTMLButtonElement | null) => {
    registerMapRef(itemActionButtonRefs.current, itemId, element);
  }, []);

  return {
    openItemMenuId,
    closeItemMenu,
    isItemMenuOpen,
    handleItemMenuToggle,
    handleItemMenuKeyDown,
    focusMenuButton,
    registerItemMenuRef,
    registerItemMenuButtonRef,
    registerItemActionRef,
  };
}
